import { FlatExtraResponse } from '../../../common/js/productService/ProductServiceClient';
import { RiderType, TimeFrequency } from '../../../common/js/static/Product';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { Utils } from '../../../common/js/Utils';
import { PremiumDisplay } from './Type';
import { PolicyData } from './PolicyData';
import { PaymentMethodType } from '@hit/policy-admin-types/dist/data-model/Types';
import { CommonUtils } from '../../../common/js/static/CommonUtils';
import { Rider } from '../../../coverpath-agent/public/js/components/home-new/quote-or-apply-new/QuoteForm';

interface TempFlatExtra {
  amount: string;
  expiresFormatted: string;
}

export class CACPaymentData {
  private readonly faceAmount: number = 0;
  private readonly waiver: boolean = false;
  private readonly waiverPremium: number = 0;
  private readonly basePremium: number = 0;
  private readonly conversionEndDate: Date = null;
  private readonly effectiveDate: Date = null;
  private readonly riders: Array<Rider> = [];
  private readonly flatExtras: Array<FlatExtraResponse> = [];
  private readonly totalPremiumFromPolicyInfo: number = 0;
  private readonly singleTempFlatExtra: TempFlatExtra;
  private readonly tempFlatExtraSum: string;
  private readonly paymentFrequency: string;
  private readonly paymentMode: string;
  private readonly exchange1035;
  private readonly isAutoPay;
  private readonly nextPaymentDate: Date = null;
  private readonly policyEndDate: Date = null;
  private readonly nextPaymentAuthorized: boolean = false;
  private readonly bankAccountLastFour: string;
  private readonly accountType: string;
  private readonly payerName: string;
  private readonly effectiveDraftDate: string;
  private readonly isBillingPaused: boolean;
  private readonly enableAuthPayforDDB: boolean;
  private readonly hasPaymentFailed: boolean = false;
  private readonly paymentFailureDate: Date = null;
  private readonly failedPaymentAmount: string;
  private readonly createdNoAuthInvoiceDate: Date = null;
  private readonly invoiceId: string;

  constructor(
    faceAmount,
    waiver,
    waiverPremium,
    basePremium,
    riders: Rider[] = [],
    flatExtras: FlatExtraResponse[] = [],
    paymentFrequency,
    paymentMethodType,
    exchange1035,
    isAutoPay,
    nextPaymentDate?,
    effectiveDate?,
    conversionEndDate?,
    totalPremiumFromPolicyInfo?,
    tempFlatExtras?: FlatExtraResponse[],
    tempFlatExtraPremimum?,
    policyEndDate?: Date,
    nextPaymentAuthorized?: boolean,
    bankAccountLastFour?: string,
    accountType?: string,
    payerName?: string,
    effectiveDraftDate?: string,
    isBillingPaused?: boolean,
    enableAuthPayforDDB?: boolean,
    hasPaymentFailed?: boolean,
    paymentFailureDate?: Date,
    failedPaymentAmount?: string,
    createdNoAuthInvoiceDate?: Date,
    invoiceId?: string
  ) {
    this.paymentFrequency = CommonUtils.makePaymentFrequencySafeForPolicy(paymentFrequency);
    this.faceAmount = faceAmount;
    this.waiver = waiver;
    this.waiverPremium = waiverPremium;
    this.basePremium = basePremium;
    this.conversionEndDate = conversionEndDate;
    this.effectiveDate = effectiveDate;
    this.riders = riders;
    this.flatExtras = flatExtras;
    this.totalPremiumFromPolicyInfo = totalPremiumFromPolicyInfo;
    this.isAutoPay = isAutoPay;
    this.nextPaymentDate = nextPaymentDate;
    this.policyEndDate = policyEndDate;
    this.nextPaymentAuthorized = nextPaymentAuthorized;
    this.bankAccountLastFour = bankAccountLastFour;
    this.accountType = accountType;
    this.payerName = payerName;
    this.effectiveDraftDate = effectiveDraftDate;
    this.isBillingPaused = isBillingPaused;
    if (tempFlatExtras) {
      if (tempFlatExtras.length === 1) {
        const fe = tempFlatExtras[0];
        const unit = fe.expiryAfterInYears === 1 ? 'year' : 'years';
        this.singleTempFlatExtra = {
          amount: Utils.formatCurrency(fe.premium),
          expiresFormatted: `${fe.expiryAfterInYears} ${unit}`,
        };
      } else if (tempFlatExtras.length > 1) {
        this.tempFlatExtraSum = Utils.formatCurrency(tempFlatExtraPremimum);
      }
    }

    if (paymentMethodType === PaymentMethodType.BankAccount) {
      this.paymentMode = 'ach';
    } else if (paymentMethodType === PaymentMethodType.Check) {
      this.paymentMode = 'check';
    } else {
      this.paymentMode = paymentMethodType;
    }

    this.exchange1035 = exchange1035;
    this.enableAuthPayforDDB = enableAuthPayforDDB;
    this.hasPaymentFailed = hasPaymentFailed;
    this.paymentFailureDate = paymentFailureDate;
    this.failedPaymentAmount = failedPaymentAmount;
    this.createdNoAuthInvoiceDate = createdNoAuthInvoiceDate;
    this.invoiceId = invoiceId;
  }

  getCACSingleTempFlatExtra() {
    return this.singleTempFlatExtra;
  }

  getCACTempFlatExtraSum() {
    return this.tempFlatExtraSum;
  }

  getCACFaceAmount() {
    return this.faceAmount;
  }

  getCACHasWaiver() {
    return this.waiver;
  }

  getCACWaiverPremium() {
    return this.waiverPremium;
  }

  getCACPolicyEndDate() {
    return this.policyEndDate;
  }

  getCACTotalWaiverPremium() {
    return this.riders.reduce((total, rider) => {
      return total + rider.waiver;
    }, this.getCACWaiverPremium());
  }

  getCACBasePremium() {
    return this.basePremium;
  }

  getCACTotalBasePremium() {
    return this.riders.reduce((total, rider) => {
      return total + rider.premium;
    }, this.getCACBasePremium());
  }

  getCACTotalFlatExtras() {
    return this.getCACAllAggregateFlatExtras().reduce((acc, fe) => {
      return acc + fe.premium;
    }, 0);
  }

  getCACTotalPremium() {
    return this.getCACTotalWaiverPremium() + this.getCACTotalBasePremium() + this.getCACTotalFlatExtras();
  }

  getCACAllAggregateFlatExtras(): Array<FlatExtraResponse> {
    const aggFlatExtras = [];
    let rtrFlatExtras = [];
    if (this.getCACHasRtrRider()) {
      rtrFlatExtras = this.getCACrtrRider().flatExtras;
    }

    this.flatExtras.forEach((fe) => {
      const found = aggFlatExtras.find((val) => val.name === fe.name);
      if (!found) {
        const premium = this.flatExtras
          .filter((fe1) => fe1.name === fe.name)
          .reduce((sum, val) => {
            return sum + val.premium;
          }, 0);

        const rtrFE = rtrFlatExtras.find((rFE) => rFE.ruleCode === fe.ruleCode);
        aggFlatExtras.push({
          name: fe.name,
          premium: premium + (rtrFE ? rtrFE.premium : 0),
        });
      }
    });

    return aggFlatExtras;
  }

  getCACConversionEndDate() {
    return this.conversionEndDate;
  }

  getCACEffectiveDate() {
    return this.effectiveDate;
  }

  getCACHasGirRider(): boolean {
    return !!this.getCACgirRider();
  }

  getCACgirRider(): Rider {
    return this.riders.find((rider) => rider.type === RiderType.GIR);
  }

  getCACHasAlirRider(): boolean {
    return !!this.getCACAlirRider();
  }

  getCACAlirRider(): Rider {
    return this.riders.find((rider) => rider.type === RiderType.ALIR);
  }

  getCACHasRtrRider(): boolean {
    return !!this.getCACrtrRider();
  }

  getCACHasCirRider(): boolean {
    return !!this.getCACCirRider();
  }

  getCACCirRider(): Rider {
    return this.riders.find((rider) => rider.type === RiderType.CIR);
  }

  getCACrtrRider(): Rider {
    return this.riders.find((rider) => rider.type === RiderType.RTR);
  }

  getCACTotalPremiumFromPolicyInfo(): number {
    return this.totalPremiumFromPolicyInfo;
  }

  getCACDisplayInfo(flatExtra: FlatExtraResponse) {
    return CoverpathUtils.getFlatExtraDisplayInfo(flatExtra.name, flatExtra.ruleCode, flatExtra.expiryAfterInYears);
  }

  getCACRiders() {
    return this.riders;
  }

  getCACPaymentFrequency() {
    return this.paymentFrequency;
  }

  getCACPaymentMode() {
    return this.paymentMode;
  }

  getCACExchange1035() {
    return this.exchange1035;
  }

  getCACIsAutoPay() {
    return this.isAutoPay;
  }

  getCACNextPaymentDate() {
    return this.nextPaymentDate;
  }

  getCACNextPaymentAuthorized() {
    return this.nextPaymentAuthorized;
  }

  getCACBankAccountLastFour() {
    return this.bankAccountLastFour;
  }

  getCACAccountType() {
    return this.accountType;
  }

  getCACPayerName() {
    return this.payerName;
  }

  getCACEffectiveDraftDate() {
    return this.effectiveDraftDate;
  }

  getCACIsBillingPaused() {
    return this.isBillingPaused;
  }

  getCACIsPaymentAuthorizationRequired() {
    return this.enableAuthPayforDDB && this.getCACPaymentFrequency() === 'annual' && !this.getCACIsAutoPay();
  }

  getCACHasPaymentFailed() {
    return this.hasPaymentFailed;
  }

  getCACPaymentFailueDate() {
    return this.paymentFailureDate;
  }

  getCACFailedPaymentAmount() {
    return this.failedPaymentAmount;
  }

  getCACCreatedNoAuthInvoiceDate() {
    return this.createdNoAuthInvoiceDate;
  }

  getCACInvoiceId() {
    return this.invoiceId;
  }

  getCACNeedsPaymentAuthorization() {
    return (
      this.getCACIsPaymentAuthorizationRequired() &&
      !this.getCACNextPaymentAuthorized() &&
      this.getCACNextPaymentDisplayDDB()
    );
  }

  getCACNeedsAuthorizationWithinWindow() {
    if (!this.getCACNeedsPaymentAuthorization()) {
      return false;
    }

    const today = Utils.now();
    const daysBetweenTodayAndNpd = Utils.daysBetweenIgnoreTime(today, this.getCACNextPaymentDisplayDDB());
    return daysBetweenTodayAndNpd > -1 && daysBetweenTodayAndNpd <= 30;
  }

  getCACNextPaymentDisplayDDBPast() {
    if (!this.getCACNeedsPaymentAuthorization()) {
      return false;
    }

    const today = Utils.now();
    return Utils.daysBetweenIgnoreTime(today, this.getCACNextPaymentDisplayDDB()) < 0;
  }

  getCACNextPaymentDisplayDDB() {
    return this.getCACCreatedNoAuthInvoiceDate() || this.getCACNextPaymentDate();
  }

  getCACNeedsAuthorizationWithinOrPast() {
    return this.getCACNeedsAuthorizationWithinWindow() || this.getCACNextPaymentDisplayDDBPast();
  }

  getCACPremiumDisplayObj(workflowAction: string, policyData: PolicyData, isForAgentSite: boolean): PremiumDisplay {
    const timeFrequency = TimeFrequency[policyData.getCACPaymentData().getCACPaymentFrequency()];
    const premiumAmount = Utils.formatCurrency(policyData.getCACPaymentData().getCACTotalPremium());
    return CoverpathUtils.getPremiumObjForPremiumAmount(
      workflowAction,
      premiumAmount,
      policyData.getCACPaymentData().getCACBasePremium(),
      timeFrequency,
      policyData.getCACIsTLIC(),
      isForAgentSite,
      policyData.getCACIsLifeBridge()
    );
  }

  getCACBankAccountClassification(): 'Checking' | 'Savings' {
    return this.accountType?.toLowerCase().includes('checking') ? 'Checking' : 'Savings';
  }
}
